import type { QueueTone } from '@/components/designSystem/QueueViews/types'
import { EFFECTIVE_SOURCE_KIND_LABELS } from '@/constants/ds-attendance.constants'
import type { EffectiveSourceKind } from '@/constants/ds-attendance.constants'
import type {
  EligibilityLeg,
  EligibilityReason,
  EligibilityReasonCode,
  PaymentEligibilityFields,
  PaymentEligibilityView,
  PaymentModality
} from '@/types/payment-eligibility.types'

export const PAYMENT_MODALITIES: PaymentModality[] = ['ANTECIPE', 'IN_CASH']

export const PAYMENT_MODALITY_LABELS: Record<PaymentModality, string> = {
  ANTECIPE: 'Antecipe',
  IN_CASH: 'À vista'
}

/**
 * Espelho de `ELIGIBILITY_REASON_LABELS` do backend (plano 02). Se o backend fixar outro
 * texto, este mapa segue o backend — a Operação precisa ler a mesma frase no painel e no
 * export.
 */
export const ELIGIBILITY_REASON_LABELS: Record<EligibilityReasonCode, string> = {
  REGISTRATION_NOT_OK: 'Registro de presença incompleto ou ainda não auditado',
  SOURCE_NOT_ACCEPTED: 'Fonte do registro não aceita pela política da escala',
  REQUIRED_APP_CHECK:
    'A escala exige check pelo App; o Registro de Atividade não libera o pagamento',
  UNIDENTIFIED_COVERAGE: 'Plantão ocupado sem identificação do médico'
}

const LEG_LABELS: Record<EligibilityLeg, string> = { CHECKIN: 'entrada', CHECKOUT: 'saída' }

/**
 * Só a FORMA é validada (objeto com `code` string e modalidade conhecida). Um código novo
 * do backend passa e aparece cru: esconder o motivo deixaria um "inelegível" sem porquê.
 */
function isReason(raw: unknown): raw is EligibilityReason {
  if (!raw || typeof raw !== 'object') return false
  const candidate = raw as { code?: unknown; modality?: unknown }
  return (
    typeof candidate.code === 'string' &&
    PAYMENT_MODALITIES.includes(candidate.modality as PaymentModality)
  )
}

/**
 * `null` = não calculado (flag `PAYMENT_ELIGIBILITY_ENABLED` desligada, plantão antes do
 * corte ou ainda não recalculado). Nunca traduzir isso para "inelegível".
 *
 * Basta UMA modalidade não calculada para a view inteira ser `null`: o selo resume as duas
 * ("Só Fluxo" afirma que nenhuma paga), então não dá para montá-lo com metade. O backend
 * grava as duas juntas; o DTO só as tipa de forma independente.
 */
export function toPaymentEligibilityView(
  raw: PaymentEligibilityFields | null | undefined
): PaymentEligibilityView | null {
  if (!raw) return null
  const { antecipeEligible, inCashEligible } = raw
  if (typeof antecipeEligible !== 'boolean' || typeof inCashEligible !== 'boolean') return null
  return {
    antecipeEligible,
    inCashEligible,
    reasons: Array.isArray(raw.eligibilityReasons) ? raw.eligibilityReasons.filter(isReason) : []
  }
}

export type EligibilitySealKind = 'BOTH' | 'ANTECIPE_ONLY' | 'IN_CASH_ONLY' | 'FLOW_ONLY'

export interface EligibilitySeal {
  kind: EligibilitySealKind
  label: string
  /** Versão curta para células estreitas (coluna "Plantão" do espelho). */
  shortLabel: string
  tone: QueueTone
}

const SEALS: Record<EligibilitySealKind, EligibilitySeal> = {
  BOTH: {
    kind: 'BOTH',
    label: 'Elegível Antecipe · À vista',
    shortLabel: 'Antecipe · À vista',
    tone: 'ok'
  },
  ANTECIPE_ONLY: {
    kind: 'ANTECIPE_ONLY',
    label: 'Elegível Antecipe',
    shortLabel: 'Antecipe',
    tone: 'info'
  },
  IN_CASH_ONLY: {
    kind: 'IN_CASH_ONLY',
    label: 'Elegível À vista',
    shortLabel: 'À vista',
    tone: 'info'
  },
  FLOW_ONLY: { kind: 'FLOW_ONLY', label: 'Só Fluxo', shortLabel: 'Só Fluxo', tone: 'warn' }
}

export function resolveEligibilitySeal(view: PaymentEligibilityView): EligibilitySeal {
  if (view.antecipeEligible && view.inCashEligible) return SEALS.BOTH
  if (view.antecipeEligible) return SEALS.ANTECIPE_ONLY
  if (view.inCashEligible) return SEALS.IN_CASH_ONLY
  return SEALS.FLOW_ONLY
}

function describeReason(reason: EligibilityReason): string {
  const base = ELIGIBILITY_REASON_LABELS[reason.code] ?? reason.code
  const details = [
    reason.leg ? LEG_LABELS[reason.leg] : null,
    reason.kind
      ? (EFFECTIVE_SOURCE_KIND_LABELS[reason.kind as EffectiveSourceKind] ?? reason.kind)
      : null
  ].filter((detail): detail is string => !!detail)
  return details.length ? `${base} (${details.join(' · ')})` : base
}

/**
 * Uma linha por motivo distinto (`code` + perna + fonte), com as modalidades afetadas na
 * frente: "Antecipe e À vista: Registro de presença incompleto ou ainda não auditado".
 */
export function formatEligibilityReasons(
  reasons: EligibilityReason[],
  modalities: PaymentModality[] = PAYMENT_MODALITIES
): string[] {
  const groups = new Map<string, { reason: EligibilityReason; found: Set<PaymentModality> }>()
  for (const reason of reasons) {
    if (!isReason(reason) || !modalities.includes(reason.modality)) continue
    const key = [reason.code, reason.leg ?? '', reason.kind ?? ''].join('|')
    const group = groups.get(key) ?? { reason, found: new Set<PaymentModality>() }
    group.found.add(reason.modality)
    groups.set(key, group)
  }
  return [...groups.values()].map(({ reason, found }) => {
    const who = PAYMENT_MODALITIES.filter((modality) => found.has(modality))
      .map((modality) => PAYMENT_MODALITY_LABELS[modality])
      .join(' e ')
    return `${who}: ${describeReason(reason)}`
  })
}

/** Espelho da folha: um selo por `doctorStationId`; o não calculado fica fora do mapa. */
export function indexEligibilityByDoctorStation(
  items: Array<PaymentEligibilityFields & { doctorStationId: number }>
): Record<number, PaymentEligibilityView> {
  const index: Record<number, PaymentEligibilityView> = {}
  for (const item of items) {
    const view = toPaymentEligibilityView(item)
    if (view && Number.isFinite(item.doctorStationId)) index[item.doctorStationId] = view
  }
  return index
}
